import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AttachmentService } from '../../services/attachment.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { FileViewerComponent, FileViewerData } from '../file-viewer/file-viewer.component';

@Component({
  selector: 'app-blob-file-list',
  templateUrl: './blob-file-list.component.html',
  styleUrl: './blob-file-list.component.scss'
})
export class BlobFileListComponent implements OnChanges {
  @Input() files: any[] = [];
  @Input() canDelete = true;
  @Output() onFileRemoved = new EventEmitter<any>();

  fileList: any[] = [];
  displayedColumns = ['icon', 'name', 'uploadedBy', 'uploadedOn', 'size', 'actions'];

  constructor(private attachmentService: AttachmentService, private dialog: MatDialog) {}

  ngOnChanges(): void {
    this.fileList = this.files ? [...this.files] : [];
  }

  getFileIcon(fileType: string, fileName: string): string {
    const type = (fileType || '').toLowerCase();
    const ext = (fileName || '').split('.').pop()?.toLowerCase() ?? '';
    if (type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image';
    if (type === 'application/pdf' || ext === 'pdf') return 'picture_as_pdf';
    if (type.includes('spreadsheet') || type.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) return 'table_chart';
    if (type.includes('wordprocessing') || type.includes('word') || ['doc', 'docx'].includes(ext)) return 'description';
    if (type.startsWith('video/') || ['mp4', 'avi', 'mov', 'mkv'].includes(ext)) return 'videocam';
    if (type.startsWith('audio/') || ['mp3', 'wav', 'aac'].includes(ext)) return 'audio_file';
    return 'attach_file';
  }

  getFileIconColor(fileType: string, fileName: string): string {
    switch (this.getFileIcon(fileType, fileName)) {
      case 'image': return '#4caf50';
      case 'picture_as_pdf': return '#f44336';
      case 'table_chart': return '#388e3c';
      case 'description': return '#1565c0';
      case 'videocam': return '#9c27b0';
      case 'audio_file': return '#ff9800';
      default: return '#757575';
    }
  }

  getDisplayName(file: any): string {
    if (file.OriginalFileName) return file.OriginalFileName;
    // Fallback for legacy records: strip folder prefix from FileName
    const segment = (file.FileName || '').split('/').pop() || file.FileName;
    return segment;
  }

  formatSize(bytes: number): string {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  openInTab(file: any): void {
    this.attachmentService.getAttachmentReadUrl(file.FileName, file.expiry, file.readUrl)?.subscribe({
      next: (result) => {
        this.cacheReadUrl(file, result);
        window.open(result.readSasUri, '_blank');
      },
      error: (err) => console.error('Error opening file:', err)
    });
  }

  open(file: any): void {
    const data: FileViewerData = {
      blobName: file.FileName,
      originalFileName: file.OriginalFileName || file.FileName,
      fileType: file.FileType,
      sasUrl: file.readUrl,
      expiresOn: file.expiry
    };
    this.dialog.open(FileViewerComponent, {
      width: '95vw',
      maxWidth: '1200px',
      maxHeight: '95vh',
      panelClass: 'file-viewer-dialog',
      data
    });
  }

  download(file: any): void {
    this.attachmentService.getAttachmentReadUrl(file.FileName, file.expiry, file.readUrl)?.subscribe({
      next: async (result) => {
        this.cacheReadUrl(file, result);
        try {
          const response = await fetch(result.readSasUri);
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = file.OriginalFileName || file.FileName;
          a.click();
          URL.revokeObjectURL(objectUrl);
        } catch (err) {
          console.error('Error downloading file:', err);
        }
      },
      error: (err) => console.error('Error fetching download URL:', err)
    });
  }

  remove(file: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      autoFocus: false,
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete "${file.OriginalFileName || file.FileName}"?`,
        cancelText: 'Cancel',
        confirmText: 'Yes, Delete'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed === true) {
        this.attachmentService.deleteFileMetadata(file.Id).subscribe({
          next: () => {
            this.fileList = this.fileList.filter(f => f.Id !== file.Id);
            this.onFileRemoved.emit(file);
          },
          error: (err) => console.error('Error deleting file:', err)
        });
      }
    });
  }

  private cacheReadUrl(file: any, result: { readSasUri: string; expiresOn: string }): void {
    const idx = this.fileList.findIndex(f => f.FileName === file.FileName);
    if (idx !== -1) {
      this.fileList[idx].readUrl = result.readSasUri;
      this.fileList[idx].expiry = result.expiresOn;
    }
  }
}
